import { env } from './env';
import { supabase } from './supabase';

/**
 * Tiny wrapper around `fetch` that injects the Supabase JWT and a base URL.
 *
 * Returns parsed JSON or throws an `ApiError` on non-2xx so callers can
 * distinguish "no network" from "auth failed" from "server said no".
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.authorization = `Bearer ${token}`;

  const url = `${env.socialGeneratorApiBase}${path}`;
  const res = await fetch(url, { ...init, headers });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(
      `${init.method ?? 'GET'} ${path} → ${res.status}`,
      res.status,
      body,
    );
  }

  if (res.status === 204) return undefined as T;
  // Some endpoints (ZIP downloads) return non-JSON; let callers opt out via
  // a `raw=true` query param or use `fetch` directly. For now the JSON
  // endpoints are the only ones that go through this wrapper.
  return (await res.json()) as T;
}
